import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';

// --- STATIC IMPORTS (Core Pages to prevent flashing) ---
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
const SolutionsPage = lazy(() => import('./pages/SolutionsPage'));
const SolutionDetailPage = lazy(() => import('./pages/SolutionDetailPage'));
import './pages/SolutionDetailPage.css'; // Remove lazy loading for SolutionDetailPage CSS only
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
import CaseStudiesPage from './pages/CaseStudiesPage';
const CaseStudyDetailPage = lazy(() => import('./pages/CaseStudyDetailPage'));

const WhyVivifyPage = lazy(() => import('./pages/WhyVivifyPage'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const EnterpriseSoftwarePage = lazy(() => import('./pages/EnterpriseSoftwarePage'));
import IndustrialSoftwarePage from './pages/IndustrialSoftwarePage';
const CustomProductDevelopmentPage = lazy(() => import('./pages/CustomProductDevelopmentPage'));
const CloudITServicesPage = lazy(() => import('./pages/CloudITServicesPage'));
const IndustrialIoTSolutionsPage = lazy(() => import('./pages/IndustrialIoTSolutionsPage'));
const SmilaiPage = lazy(() => import('./pages/SmilaiPage'));
const FactoraiPage = lazy(() => import('./pages/FactoraiPage'));
const SecuraiPage = lazy(() => import('./pages/SecuraiPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const SelfDrivingErpArticlePage = lazy(() => import('./pages/SelfDrivingErpArticlePage'));
const AIDrivenErpArticlePage = lazy(() => import('./pages/AIDrivenErpArticlePage'));
const HyperautomationErpArticlePage = lazy(() => import('./pages/HyperautomationErpArticlePage'));
const OmnichannelEcommerceArticlePage = lazy(() => import('./pages/OmnichannelEcommerceArticlePage'));
const AutomationToAutonomyArticlePage = lazy(() => import('./pages/AutomationToAutonomyArticlePage'));
const DigitalTransformationArticlePage = lazy(() => import('./pages/DigitalTransformationArticlePage'));
const BuildingFairTrustworthyEnterpriseAIPage = lazy(() => import('./pages/BuildingFairTrustworthyEnterpriseAIPage'));
const AIWorkflowAutomationArticlePage = lazy(() => import('./pages/AIWorkflowAutomationArticlePage'));
const StampingLineMicroStopsPage = lazy(() => import('./pages/StampingLineMicroStopsPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import useScrollReveal from './hooks/useScrollReveal';

const GlobalModals = lazy(() => import('./components/GlobalModals'));

function App() {
  // Initialize scroll reveal hooks
  useScrollReveal('revealed');
  const { pathname } = useLocation();
  const hideGlobalFooter = /^\/case-studies\/[^/]+$/.test(pathname);

  // Dynamically update Canonical Tag for SPA navigation (fixes SEO extension warnings)
  useEffect(() => {
    let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link') as HTMLLinkElement;
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    // Remove trailing slash for consistency with prerender-seo.js
    const safePath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
    link.href = `https://www.vivifysoft.com${safePath}`;
  }, [pathname]);

  // Single active modal — only one can ever be open at a time
  const [activeModal, setActiveModal] = useState<null | 'consult' | 'demo' | 'resume'>(null);
  const closeModal = () => setActiveModal(null);

  // Demo Product State
  const [selectedProduct, setSelectedProduct] = useState('');

  // Trigger consultation from any child CTA button
  const triggerConsultation = () => {
    setActiveModal('consult');
  };

  // Trigger demo request from any product card
  const triggerDemo = (productName: string) => {
    setSelectedProduct(productName);
    setActiveModal('demo');
  };



  // Trigger resume submission from Careers section
  const triggerResumeSubmission = () => {
    setActiveModal('resume');
  };

  return (
    <>
      <ScrollToTop />
      <CookieBanner />
      <Navbar />

      <Suspense fallback={<main />}>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                onBookConsultation={triggerConsultation}
                onRequestDemo={triggerDemo}
                onApply={triggerResumeSubmission}
              />
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/why-vivify" element={<WhyVivifyPage />} />
          <Route
            path="/services"
            element={
              <ServicesPage
                onBookConsultation={triggerConsultation}
              />
            }
          />

          <Route
            path="/services/enterprise-software-solutions"
            element={
              <EnterpriseSoftwarePage
                onBookConsultation={triggerConsultation}
              />
            }
          />
          <Route
            path="/services/industrial-software-automation"
            element={
              <IndustrialSoftwarePage
                onBookConsultation={triggerConsultation}
              />
            }
          />
          <Route
            path="/services/custom-product-development"
            element={
              <CustomProductDevelopmentPage
                onStartProject={triggerConsultation}
              />
            }
          />
          <Route
            path="/services/cloud-it-services"
            element={
              <CloudITServicesPage
                onBookConsultation={triggerConsultation}
              />
            }
          />
          <Route
            path="/services/:serviceId"
            element={
              <ServiceDetailPage
                onBookConsultation={triggerConsultation}
                onRequestDemo={triggerDemo}
              />
            }
          />
          <Route
            path="/solutions"
            element={
              <SolutionsPage
                onBookConsultation={triggerConsultation}
              />
            }
          />
          <Route
            path="/solutions/industrial-iot-solutions"
            element={
              <IndustrialIoTSolutionsPage
                onBookConsultation={triggerConsultation}
              />
            }
          />
          <Route
            path="/solutions/:solutionId"
            element={
              <SolutionDetailPage
                onBookConsultation={triggerConsultation}
                onRequestDemo={triggerDemo}
              />
            }
          />
          <Route
            path="/products"
            element={<ProductsPage />}
          />
          <Route
            path="/products/smilai"
            element={<SmilaiPage onRequestDemo={triggerDemo} />}
          />
          <Route
            path="/products/factorai"
            element={<FactoraiPage onRequestDemo={triggerDemo} />}
          />
          <Route
            path="/products/securai"
            element={<SecuraiPage onRequestDemo={triggerDemo} />}
          />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/self-driving-erp" element={<SelfDrivingErpArticlePage />} />
          <Route path="/insights/ai-driven-erp-mid-sized-businesses" element={<AIDrivenErpArticlePage />} />
          <Route path="/insights/hyperautomation-in-erp" element={<HyperautomationErpArticlePage />} />
          <Route path="/insights/omnichannel-ecommerce-development" element={<OmnichannelEcommerceArticlePage />} />
          <Route path="/insights/automation-to-autonomy-erp" element={<AutomationToAutonomyArticlePage />} />
          <Route path="/insights/digital-transformation-explained" element={<DigitalTransformationArticlePage />} />
          <Route path="/insights/building-fair-trustworthy-enterprise-ai" element={<BuildingFairTrustworthyEnterpriseAIPage />} />
          <Route path="/insights/ai-powered-workflow-automation" element={<AIWorkflowAutomationArticlePage />} />
          <Route path="/insights/stamping-line-micro-stops" element={<StampingLineMicroStopsPage />} />
          <Route path="/insights/stamping-line-micro-stops-capacity-loss" element={<Navigate to="/insights/stamping-line-micro-stops" replace />} />
          <Route
            path="/careers"
            element={<CareersPage onApply={triggerResumeSubmission} />}
          />
          <Route
            path="/contact"
            element={<ContactPage onBookConsultation={triggerConsultation} />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />


          {/* 404 Catch-All Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {!hideGlobalFooter && <Footer />}

      {/* Global Modals (Lazy Loaded) */}
      <Suspense fallback={null}>
        {activeModal !== null && (
          <GlobalModals
            activeModal={activeModal}
            closeModal={closeModal}
            selectedProduct={selectedProduct}
          />
        )}
      </Suspense>
    </>
  );
}

export default App;
